import { AddressInput } from '../../interfaces/card';

type PlaceComponents = google.maps.GeocoderAddressComponent[];

/** Fields compared by the backend to treat two addresses of one list as the same */
const ADDRESS_IDENTITY_KEYS = [
  'country_code',
  'region',
  'district',
  'city',
  'street',
  'building',
  'apartment',
  'postal_code',
] as const;

/** Place fields requested from Google: everything `addressFromPlace` reads */
export const ADDRESS_PLACE_FIELDS = ['address_components', 'formatted_address', 'place_id', 'geometry.location'];

/**
 * Display string of an address: `formatted_address`, or one built from the parts when it is missing.
 * Google does not put the apartment into `formatted_address`, so it is appended with `apartmentLabel`.
 */
export function formatAddress(address: AddressInput | null | undefined, apartmentLabel = ''): string {
  if (!address) return '';

  const apartment = clean(address.apartment);
  const apartmentText = apartment ? [apartmentLabel.trim(), apartment].filter(Boolean).join(' ') : '';
  const formatted = clean(address.formatted_address);
  if (formatted) return [formatted, apartmentText].filter(Boolean).join(', ');

  const street = [clean(address.street), clean(address.building)].filter(Boolean).join(', ');
  return [
    street,
    apartmentText,
    clean(address.city),
    clean(address.district),
    clean(address.region),
    clean(address.postal_code),
    clean(address.country_code),
  ]
    .filter(Boolean)
    .join(', ');
}

/** Address in the request shape: response-only fields (`id`, `type`, `is_primary`, ...) are dropped */
export function toAddressInput(address: AddressInput): AddressInput {
  const hasCoordinates = typeof address.latitude === 'number' && typeof address.longitude === 'number';

  return {
    country_code: clean(address.country_code).toUpperCase(),
    region: clean(address.region) || null,
    district: clean(address.district) || null,
    city: clean(address.city) || null,
    street: clean(address.street) || null,
    building: clean(address.building) || null,
    apartment: clean(address.apartment) || null,
    postal_code: clean(address.postal_code) || null,
    formatted_address: clean(address.formatted_address) || null,
    place_id: clean(address.place_id) || null,
    latitude: hasCoordinates ? address.latitude : null,
    longitude: hasCoordinates ? address.longitude : null,
  };
}

/** Same rule as the backend: case-insensitive match of the address parts, ignoring formatting and coordinates */
export function getAddressIdentity(address: AddressInput): string {
  return ADDRESS_IDENTITY_KEYS.map((key) => clean(address[key]).toLowerCase()).join('|');
}

/** Drops repeated addresses, keeping the first one, as the backend does */
export function uniqueAddresses(addresses: AddressInput[]): AddressInput[] {
  const seen = new Set<string>();

  return addresses.filter((address) => {
    const identity = getAddressIdentity(address);
    if (seen.has(identity)) return false;

    seen.add(identity);
    return true;
  });
}

export function isSameAddressList(a: AddressInput[] | null | undefined, b: AddressInput[] | null | undefined): boolean {
  const left = (a ?? []).map(getAddressIdentity).sort();
  const right = (b ?? []).map(getAddressIdentity).sort();

  return left.length === right.length && left.every((identity, index) => identity === right[index]);
}

export function getAddressMapUrl(address: AddressInput): string {
  const params = new URLSearchParams({ api: '1', query: formatAddress(address) });
  const placeId = clean(address.place_id);
  if (placeId) params.set('query_place_id', placeId);

  return `https://www.google.com/maps/search/?${params.toString()}`;
}

/** Maps a Google place to an address; `null` when the place has no country */
export function addressFromPlace(place: google.maps.places.PlaceResult): AddressInput | null {
  const components = place.address_components ?? [];
  const countryCode = getComponent(components, 'country', 'short_name');
  if (!countryCode) return null;

  const location = place.geometry?.location;

  return toAddressInput({
    country_code: countryCode,
    region: getComponent(components, 'administrative_area_level_1'),
    district: getComponent(components, 'administrative_area_level_2'),
    city:
      getComponent(components, 'locality') ||
      getComponent(components, 'postal_town') ||
      getComponent(components, 'administrative_area_level_3') ||
      getComponent(components, 'sublocality'),
    street: getComponent(components, 'route'),
    building: getComponent(components, 'street_number'),
    apartment: getComponent(components, 'subpremise'),
    postal_code: getComponent(components, 'postal_code'),
    formatted_address: place.formatted_address ?? null,
    place_id: place.place_id ?? null,
    latitude: location ? location.lat() : null,
    longitude: location ? location.lng() : null,
  });
}

function getComponent(
  components: PlaceComponents,
  type: string,
  name: 'long_name' | 'short_name' = 'long_name',
): string {
  return components.find((component) => component.types.includes(type))?.[name] ?? '';
}

function clean(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
